import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { adminApi, type UserStatus } from '@/features/admin/admin.api';
import type { UserRole } from '@/store/authSlice';

type Tab = 'users' | 'meetings' | 'subscriptions' | 'audit';
const TABS: { id: Tab; label: string }[] = [
  { id: 'users', label: 'Users' },
  { id: 'meetings', label: 'Meetings' },
  { id: 'subscriptions', label: 'Subscriptions' },
  { id: 'audit', label: 'Audit Logs' },
];

export default function AdminDashboardPage(): JSX.Element {
  const [tab, setTab] = useState<Tab>('users');
  return (
    <div>
      <h1 className="text-2xl font-bold">Admin Dashboard</h1>
      <div className="mt-4 flex gap-2 border-b border-slate-200 dark:border-slate-800">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm ${tab === t.id ? 'border-b-2 border-brand-600 font-medium text-brand-600' : 'text-slate-500'}`}
          >
            {t.label}
          </button>
        ))}
      </div>
      <div className="mt-6">
        {tab === 'users' && <UsersTab />}
        {tab === 'meetings' && <MeetingsTab />}
        {tab === 'subscriptions' && <SubscriptionsTab />}
        {tab === 'audit' && <AuditTab />}
      </div>
    </div>
  );
}

function UsersTab(): JSX.Element {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const { data, isLoading } = useQuery({ queryKey: ['admin', 'users', search], queryFn: () => adminApi.users({ search }) });
  const roleMut = useMutation({
    mutationFn: ({ id, role }: { id: string; role: UserRole }) => adminApi.setRole(id, role),
    onSuccess: () => { toast.success('Role updated'); qc.invalidateQueries({ queryKey: ['admin', 'users'] }); },
    onError: () => toast.error('Not permitted'),
  });
  const statusMut = useMutation({
    mutationFn: ({ id, status }: { id: string; status: UserStatus }) => adminApi.setStatus(id, status),
    onSuccess: () => { toast.success('Status updated'); qc.invalidateQueries({ queryKey: ['admin', 'users'] }); },
  });

  return (
    <div>
      <input className="input mb-4 max-w-sm" placeholder="Search users…" value={search} onChange={(e) => setSearch(e.target.value)} />
      {isLoading ? <p className="text-sm text-slate-500">Loading…</p> : (
        <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-800">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left dark:bg-slate-800/50">
              <tr><th className="p-3">Name</th><th className="p-3">Email</th><th className="p-3">Role</th><th className="p-3">Status</th><th className="p-3">Actions</th></tr>
            </thead>
            <tbody>
              {data?.items.map((u) => (
                <tr key={u.id} className="border-t border-slate-100 dark:border-slate-800">
                  <td className="p-3">{u.firstName} {u.lastName}</td>
                  <td className="p-3 text-slate-500">{u.email}</td>
                  <td className="p-3">
                    <select className="input py-1" value={u.role} onChange={(e) => roleMut.mutate({ id: u.id, role: e.target.value as UserRole })}>
                      {['GUEST', 'USER', 'ADMIN', 'SUPER_ADMIN'].map((r) => <option key={r}>{r}</option>)}
                    </select>
                  </td>
                  <td className="p-3">{u.status}</td>
                  <td className="p-3">
                    {u.status === 'SUSPENDED' ? (
                      <button className="text-green-600" onClick={() => statusMut.mutate({ id: u.id, status: 'ACTIVE' })}>Activate</button>
                    ) : (
                      <button className="text-red-600" onClick={() => statusMut.mutate({ id: u.id, status: 'SUSPENDED' })}>Suspend</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function MeetingsTab(): JSX.Element {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['admin', 'meetings'], queryFn: () => adminApi.meetings() });
  const endMut = useMutation({
    mutationFn: (id: string) => adminApi.endMeeting(id),
    onSuccess: () => { toast.success('Meeting ended'); qc.invalidateQueries({ queryKey: ['admin', 'meetings'] }); },
  });
  if (isLoading) return <p className="text-sm text-slate-500">Loading…</p>;
  return (
    <div className="space-y-2">
      {data?.items.map((m) => (
        <div key={m.id} className="card flex items-center justify-between py-3">
          <div><span className="font-medium">{m.title}</span> <span className="ml-2 text-xs text-slate-500">{m.status} · {m.meetingCode}</span></div>
          {(m.status === 'LIVE' || m.status === 'SCHEDULED') && (
            <button className="text-sm text-red-600" onClick={() => endMut.mutate(m.id)}>Force end</button>
          )}
        </div>
      ))}
    </div>
  );
}

function SubscriptionsTab(): JSX.Element {
  const { data, isLoading } = useQuery({ queryKey: ['admin', 'subs'], queryFn: () => adminApi.subscriptions() });
  if (isLoading) return <p className="text-sm text-slate-500">Loading…</p>;
  return (
    <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-800">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-left dark:bg-slate-800/50">
          <tr><th className="p-3">User</th><th className="p-3">Plan</th><th className="p-3">Status</th><th className="p-3">Renews</th></tr>
        </thead>
        <tbody>
          {data?.items.map((s) => (
            <tr key={s.id} className="border-t border-slate-100 dark:border-slate-800">
              <td className="p-3">{s.user.email}</td>
              <td className="p-3 font-medium">{s.tier}</td>
              <td className="p-3">{s.status}</td>
              <td className="p-3 text-slate-500">{s.currentPeriodEnd ? new Date(s.currentPeriodEnd).toLocaleDateString() : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AuditTab(): JSX.Element {
  const { data, isLoading } = useQuery({ queryKey: ['admin', 'audit'], queryFn: () => adminApi.auditLogs() });
  if (isLoading) return <p className="text-sm text-slate-500">Loading…</p>;
  return (
    <div className="space-y-1 font-mono text-xs">
      {data?.items.map((l) => (
        <div key={l.id} className="flex gap-3 rounded border border-slate-100 p-2 dark:border-slate-800">
          <span className="text-slate-400">{new Date(l.createdAt).toLocaleString()}</span>
          <span className="font-semibold text-brand-600">{l.action}</span>
          <span className="text-slate-500">{l.resource}</span>
          <span className="ml-auto text-slate-400">{l.ipAddress}</span>
        </div>
      ))}
    </div>
  );
}
